// `buildOgMetadata(surface, opts)` — returns the `openGraph` and
// `twitter` blocks of a Next.js `Metadata` object, ready to merge into a
// page's `generateMetadata` result. Single helper used by every page
// that owns a shareable URL.

import type { Metadata } from 'next';
import { getLocale, getTranslations } from 'next-intl/server';

import type { Locale } from '@/i18n/config';
import { normalizeShareSurface, type ShareSurfaceInput } from '@/lib/share/events';

import { OG_SURFACE_REGISTRY, type OgSurfaceDescriptor } from '@/lib/share/og/registry';
import { OG_DIMENSIONS } from '@/lib/share/og/tokens';
import { getOgImageUrl } from '@/lib/share/og/url';

import copy from './copy';
import { buildCanonicalUrl, buildShareUrl } from './origin';

const SITE_NAME: string = copy.site.shortName;
const TWITTER_HANDLE: string = copy.site.twitterHandle;

/** Map ISO-639 codes (next-intl `Locale`) to OG-format `language_REGION`
 *  identifiers per `og:locale` spec (https://ogp.me/#optional). The
 *  region is the most populous market for each language — Google parses
 *  the language part for ranking signal; the region is mostly cosmetic. */
const OG_LOCALE_BY_ISO: Record<Locale, string> = {
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  zh: 'zh_CN',
  ja: 'ja_JP',
  ko: 'ko_KR',
  pt: 'pt_BR',
  de: 'de_DE',
  hi: 'hi_IN',
  ru: 'ru_RU',
  tr: 'tr_TR',
  it: 'it_IT',
  id: 'id_ID',
  vi: 'vi_VN',
  nl: 'nl_NL',
  pl: 'pl_PL',
  th: 'th_TH',
  uk: 'uk_UA',
  ro: 'ro_RO',
  tl: 'tl_PH',
  sv: 'sv_SE',
};
const FALLBACK_OG_LOCALE = 'en_US';

async function resolveOgLocales(): Promise<{ current: string; alternates: readonly string[] }> {
  try {
    const iso = (await getLocale()) as Locale;
    const current = OG_LOCALE_BY_ISO[iso] ?? FALLBACK_OG_LOCALE;
    const alternates = Object.values(OG_LOCALE_BY_ISO).filter((l) => l !== current);
    return { current, alternates };
  } catch {
    // SILENT-CATCH: Some routes execute outside next-intl request scope; emit safe fallback locales.
    // `getLocale()` throws outside a next-intl request scope (e.g. on
    // pages outside the `[locale]/` segment such as `/r/[code]`). Fall
    // back to en_US + emit ALL non-en alternates so crawlers still see
    // the multi-language signal.
    const alternates = Object.values(OG_LOCALE_BY_ISO).filter((l) => l !== FALLBACK_OG_LOCALE);
    return { current: FALLBACK_OG_LOCALE, alternates };
  }
}

export interface BuildOgMetadataOpts {
  /** Page title — surfaces in `og:title` and `twitter:title`. */
  title: string;
  /** Optional page description — surfaces in `og:description` and
   *  `twitter:description`. */
  description?: string;
  /** Alt text for `og:image:alt`. Should be a localized, human-readable
   *  description of the OG image (e.g. `"Trade prediction on @morfimarkets"`).
   *  When omitted, the helper resolves the surface's `altKey` from the
   *  registry via `getTranslations`, then falls back to `title`. */
  alt?: string;
  /** Optional canonical share path (relative, e.g. `/gifts/abc123`). When
   *  provided, used to construct the canonical and `og:url` fields. When
   *  omitted, neither URL is emitted. */
  canonicalPath?: string;
  /** Optional search-param string (without leading `?`) appended to the
   *  `og:url` / `twitter:url` ONLY. Used for share-card variants where the
   *  variant URL with discriminators (`?pos=`, `?prize=`, `?claimed=`)
   *  must round-trip through unfurled clicks so social re-fetches re-emit
   *  the variant card. */
  ogUrlSearchParams?: string;
  /** Optional search-param string (without leading `?`) appended to the
   *  canonical URL. Reserved for noindex share-card variants that need a
   *  distinct cache identity from the bare page on social platforms. */
  canonicalSearchParams?: string;
  /** When true, appends `?demo-mode` to the OG image URL so the OG route
   *  handler renders the [DEMO MODE] badge and fetches from devnet.
   *  Surface helpers set this when the page itself is a demo-mode URL. */
  demoMode?: boolean;
}

/** Resolve alt text in priority order:
 *   1. caller-supplied `opts.alt`
 *   2. registry `altKey` translated via `next-intl`
 *   3. fall back to `opts.title` */
async function resolveAltText(
  input: ShareSurfaceInput,
  opts: BuildOgMetadataOpts,
): Promise<string> {
  if (typeof opts.alt === 'string' && opts.alt.length > 0) return opts.alt;
  const surface = normalizeShareSurface(input);
  // ALLOW-UNKNOWN: Mapped-type per-key descriptor is narrower than `OgSurfaceDescriptor<ShareSurface>`; same pattern as `lib/og/url.ts`. Exhaustiveness is compile-time guaranteed.
  const descriptor = OG_SURFACE_REGISTRY[surface.kind] as unknown as OgSurfaceDescriptor;
  const altKey = descriptor.altKey;
  try {
    const t = await getTranslations();
    // ALLOW-UNKNOWN: `t` is generically typed against `IntlMessages`, but registry alt keys are runtime strings; cast through unknown to call dynamically. Missing keys fall back to the title via the check below.
    const translate = t as unknown as (key: string) => string;
    const translated = translate(altKey);
    // `next-intl` returns the key path itself when the message is
    // missing; treat that as "unresolved" and fall back to title.
    if (translated.length > 0 && translated !== altKey) return translated;
  } catch {
    // SILENT-CATCH: Translation context is unavailable in some static/test contexts; fall back to title.
    // `getTranslations` can throw outside a request scope (e.g. during
    // static analysis or in tests). Fall through to the title fallback.
  }
  return opts.title;
}

/** Detect the Next.js metadata-resolver bug where any `og:url` whose
 *  resolved pathname is `/` gets emitted as just the bare origin —
 *  search params and trailing slash are stripped. See
 *  `next/dist/lib/metadata/resolvers/resolve-url.js#resolveAbsoluteUrlWithPathname`:
 *  `result.pathname === '/' ? result.origin : result.href`.
 *
 *  Workaround: when this fires, we set `metadataBase: null` on the
 *  page-level Metadata. With no metadataBase, the resolver short-
 *  circuits at `metadataBase ? resolveUrl(url, metadataBase) : url` and
 *  treats `result` as a string, then takes the `typeof result === 'string'`
 *  branch which assigns the URL through verbatim — preserving the
 *  search params. The trade-off: relative paths in other metadata
 *  fields can no longer rely on layout's metadataBase, so this MUST
 *  only fire for the narrow buggy case. We expose the resolved
 *  metadataBase override on the return so the surface helper can merge
 *  it into the page's Metadata output. */
function isMangledByNextOgUrlResolver(shareUrl: string): boolean {
  try {
    const u = new URL(shareUrl);
    return u.pathname === '/' && u.search.length > 0;
  } catch {
    // SILENT-CATCH: malformed share URL — let the caller's normal
    // metadata path handle it; this helper only flags a known Next.js
    // resolver bug for well-formed URLs.
    return false;
  }
}

export async function buildOgMetadata(
  input: ShareSurfaceInput,
  opts: BuildOgMetadataOpts,
): Promise<
  Pick<Metadata, 'description' | 'openGraph' | 'twitter' | 'alternates' | 'other' | 'metadataBase'>
> {
  const url = getOgImageUrl(input, { demoMode: opts.demoMode === true });
  // Parallelize the two next-intl awaits — both are in-process cache hits but
  // run independently. Saves a small amount of TTFB on every SEO-bearing page.
  const [alt, ogLocales] = await Promise.all([resolveAltText(input, opts), resolveOgLocales()]);
  // Two distinct URLs:
  //   - canonical strips `?demo-mode` (Google never indexes demo URLs)
  //   - og:url / twitter:url PRESERVES `?demo-mode` so unfurled devnet
  //     shares route the recipient to the devnet experience (middleware
  //     flips the cookie when the URL has the flag).
  const canonicalUrlBase =
    typeof opts.canonicalPath === 'string' && opts.canonicalPath.length > 0
      ? buildCanonicalUrl(opts.canonicalPath)
      : undefined;
  const canonicalUrl =
    canonicalUrlBase !== undefined &&
    typeof opts.canonicalSearchParams === 'string' &&
    opts.canonicalSearchParams.length > 0
      ? `${canonicalUrlBase}?${opts.canonicalSearchParams}`
      : canonicalUrlBase;
  const shareUrlBase =
    typeof opts.canonicalPath === 'string' && opts.canonicalPath.length > 0
      ? buildShareUrl(opts.canonicalPath, opts.demoMode === true)
      : undefined;
  // Append share-variant discriminator query-params to og:url. When set,
  // click-throughs from social unfurls re-fetch the variant URL and the
  // parent surface helper re-routes to the correct share-card. Without
  // these, an unfurled share-variant URL re-emits the bare market card on
  // the next crawl and the original variant is lost.
  const shareUrl =
    shareUrlBase !== undefined &&
    typeof opts.ogUrlSearchParams === 'string' &&
    opts.ogUrlSearchParams.length > 0
      ? `${shareUrlBase}?${opts.ogUrlSearchParams}`
      : shareUrlBase;
  // When Next's metadata resolver would mangle `og:url` (homepage `/` +
  // `?demo-mode` — see `isMangledByNextOgUrlResolver` doc above), set
  // `metadataBase: null` so the resolver leaves the absolute URL string
  // untouched. We still pass the absolute shareUrl through so other
  // resolvers (image URLs, canonical) keep working — those are already
  // absolute strings produced by `resolveCanonicalOrigin()`.
  const ogUrlMangled = shareUrl !== undefined && isMangledByNextOgUrlResolver(shareUrl);

  return {
    // Hoist description to the top-level Metadata so the `<meta name="description">`
    // tag (read by Schema.org / Basic-HTML scrapers, Google snippets, search
    // engines that ignore OG) doesn't fall back to the layout-level tagline.
    // Pages that also set their own description override this — Next.js
    // takes the later value during metadata merging, so callers don't have
    // to remember to remove it.
    ...(opts.description !== undefined ? { description: opts.description } : {}),
    openGraph: {
      type: 'website',
      title: opts.title,
      ...(opts.description !== undefined ? { description: opts.description } : {}),
      ...(shareUrl !== undefined ? { url: shareUrl } : {}),
      siteName: SITE_NAME,
      locale: ogLocales.current,
      // og:locale:alternate emission for the other 20 supported locales.
      // With `localePrefix: 'never'` every locale serves the same URL,
      // so we can't emit hreflang as separate URLs. This is the closest
      // crawler signal: "this URL is also available in <each>".
      alternateLocale: [...ogLocales.alternates],
      images: [
        {
          url,
          width: OG_DIMENSIONS.width,
          height: OG_DIMENSIONS.height,
          alt,
          type: 'image/jpeg',
        },
      ],
    },
    twitter: {
      card: 'summary_large_image',
      site: TWITTER_HANDLE,
      creator: TWITTER_HANDLE,
      title: opts.title,
      ...(opts.description !== undefined ? { description: opts.description } : {}),
      images: [{ url, alt }],
    },
    // Canonical URL — surfaces the page in Google Search Console + cleans
    // up tracking parameters in shares.
    //
    // hreflang language alternates are emitted directly as JSX <link>
    // tags from the root layout (`<HreflangAlternates>`). Next.js's
    // `Metadata.alternates.languages` field dedupes 22 same-href entries
    // down to a single x-default — invisible to crawlers — so the
    // metadata API can't carry the multi-locale signal here.
    ...(canonicalUrl !== undefined ? { alternates: { canonical: canonicalUrl } } : {}),
    // Legacy/fallback meta tags some scrapers (Discord embed, Apple Messages
    // older versions, certain link previewers) read instead of the OG block.
    other: {
      image: url,
      'thumbnail-url': url,
    },
    // Bypass the Next.js metadata resolver bug for the homepage demo-
    // mode share URL. See `isMangledByNextOgUrlResolver` above.
    ...(ogUrlMangled ? { metadataBase: null } : {}),
  };
}
